import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { isTauri } from "@tauri-apps/api/core";
import { routeLinkHandler } from "../../routeNav";
import { checkForUpdates } from "../../services/updater";

interface Props {
  onClose: () => void;
}

// Build SHA is injected at bundle time by Vite (define in vite.config); falls
// back to "dev" when running unbundled. Version comes from the root package.json
// via a NAMED import: Vite's json.namedExports emits one export per top-level
// key, so only `version` survives tree-shaking. A default import pulls in the
// whole manifest object, which shipped every dependency, devDependency and
// security override version to every visitor of the web and desktop app.
import { version } from "../../../../../package.json";

const BUILD_SHA: string =
  (import.meta as unknown as { env?: { VITE_BUILD_SHA?: string } }).env?.VITE_BUILD_SHA || "dev";

export function AboutPanel({ onClose }: Props) {
  // Desktop-only: the web app (app.steerly.ai) can't self-update.
  const desktop = isTauri();
  const [checking, setChecking] = useState(false);

  async function onCheckForUpdates(): Promise<void> {
    setChecking(true);
    try {
      // Manual check: shows "you're up to date" or the install prompt
      // (and ignores any earlier "Later" skip for this version).
      await checkForUpdates();
    } catch (err) {
      // checkForUpdates surfaces its own dialogs; this guards against an
      // unexpected throw (e.g. a Tauri plugin import failing) so the
      // click never becomes an unhandled rejection.
      console.error("[updater] manual check failed", err);
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="settings-panel">
      <h3>About</h3>
      <p className="settings-about__title">Steerly Workbench</p>
      <p className="settings-about__meta">
        Version {version} &middot; Build {BUILD_SHA}
      </p>

      {desktop && (
        <>
          <hr className="settings-divider" />
          <h4 className="settings-subhead">Updates</h4>
          <p className="settings-help">Steerly checks for updates automatically on launch.</p>
          <button className="button" disabled={checking} onClick={onCheckForUpdates}>
            {checking ? "Checking…" : "Check for Updates"}
          </button>
        </>
      )}

      <hr className="settings-divider" />
      <h4 className="settings-subhead">Admin Console</h4>
      <p className="settings-help">Manage policies, DLP patterns, and admin auth.</p>
      <a
        className="button primary settings-admin-link"
        href="/security-center?tab=manage"
        onClick={(e) => {
          onClose();
          routeLinkHandler("/security-center?tab=manage")(e);
        }}
      >
        Open Admin Console <ExternalLink size={14} />
      </a>
    </div>
  );
}
